import classnames from 'classnames/bind';
import EmailValidator from 'email-validator';
import PropTypes from 'prop-types';
import React from 'react';

import FloatingLabelInput from 'presentation/components/FloatingLabelInput';
import Header from 'presentation/components/Header';
import overrideStyle from 'lib/overrideStyle';

import styles from 'presentation/components/styles/Onboarding.css';
let cx = classnames.bind(styles);
let ariaLabel;

class Onboarding extends React.Component {
  constructor(props) {
    super(props);

    Onboarding.propTypes = {
      blankFieldError: PropTypes.string.isRequired,
      emailField: PropTypes.string.isRequired,
      error: PropTypes.string,
      hasEmailError: PropTypes.bool,
      header: PropTypes.string.isRequired,
      introMessage: PropTypes.string.isRequired,
      invalidEmailError: PropTypes.string.isRequired,
      nameField: PropTypes.string.isRequired,
      onSubmit: PropTypes.func.isRequired,
      submitButton: PropTypes.string.isRequired,
    };

    this.state = {
      name: null,
      hasNameError: false,
      email: null,
      hasEmailError: props.hasEmailError || false,
      error: props.error || null,
      isSubmitting: false,
    };

    this.handleNameChange = this.handleNameChange.bind(this);
    this.handleEmailChange = this.handleEmailChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleKeyPress = this.handleKeyPress.bind(this);
    this.validate = this.validate.bind(this);
  }

  static getDerivedStateFromProps(nextProps) {
    if (nextProps.hasEmailError) {
      return {
        error: nextProps.error,
        hasEmailError: nextProps.hasEmailError || false,
      };
    }
    return { isSubmitting: false };
  }

  handleNameChange(ev) {
    this.setState({ name: ev.target.value, hasNameError: false, error: null });
  }

  handleEmailChange(ev) {
    this.setState({ email: ev.target.value, hasEmailError: false, error: null });
  }

  handleKeyPress(ev) {
    if (this.state.isSubmitting) {
      return;
    }
    let charCode = ev.which || ev.keyCode;
    if (charCode === 13) {
      this.handleSubmit();
      ev.stopPropagation();
      ev.preventDefault();
      return false;
    }
  }

  handleSubmit() {
    this.setState({ isSubmitting: true });
    if (this.validate()) {
      this.props.onSubmit(this.state.name.trim(), this.state.email.trim());
    } else {
      this.setState({ isSubmitting: false });
    }
  }

  validate() {
    let hasValidFields = true;
    const email = this.state.email;
    const name = this.state.name;
    if (!email || !name) {
      hasValidFields = false;
      this.setState({ hasNameError: !name, hasEmailError: !email, error: this.props.blankFieldError });
      ariaLabel = this.props.blankFieldError;
    } else if (!isValidEmail(email)) {
      hasValidFields = false;
      this.setState({ hasEmailError: true, error: this.props.invalidEmailError });
      ariaLabel = this.props.invalidEmailError;
    }

    return hasValidFields;
  }

  render() {
    let aId = this.state.error ? 'gladlyChatOnboardingError' : 'gladlyChatOnboardingWelcome';

    let submitButtonClasses = cx(
      styles.onboarding_submitButton,
      'onboarding_submitButton',
      'onboarding_submitButton_selector'
    );
    let onboardingPaneClasses = cx(styles.onboarding, 'onboarding_selector');

    let onboardingMessageClasses = cx('onboarding_message', {
      onboarding_error: this.state.error,
    });

    let inputBoxNameClasses = cx('onboarding_inputBox_name', 'onboarding_inputBox', {
      onboarding_inputBox_error: this.state.hasNameError,
    });

    let inputBoxEmailClasses = cx('onboarding_inputBox_email', 'onboarding_inputBox', {
      onboarding_inputBox_error: this.state.hasEmailError,
    });

    return (
      <div className={onboardingPaneClasses} data-aid="gladlyChatOnboardingPane">
        <Header isOnboarding title={this.props.header} />
        <div className={styles.onboarding_body}>
          <IntroMessageWrapper className={onboardingMessageClasses} data-aid={aId} aria-label={ariaLabel} role="alert">
            {this.state.error ? this.state.error : this.props.introMessage}
          </IntroMessageWrapper>
          <FloatingLabelInput
            aria-label="name"
            id="id_name"
            role="form"
            className={inputBoxNameClasses}
            label={this.props.nameField}
            onChange={this.handleNameChange}
            onKeyPress={this.handleKeyPress}
            shrink={70}
            value={this.state.name}
            data-aid="gladlyChatOnboardingNameInput"
            type="text"
          />
          <FloatingLabelInput
            aria-label="email"
            id="id_email"
            role="form"
            className={inputBoxEmailClasses}
            type="email"
            label={this.props.emailField}
            onChange={this.handleEmailChange}
            onKeyPress={this.handleKeyPress}
            shrink={70}
            value={this.state.email}
            data-aid="gladlyChatOnboardingEmailInput"
          />
          <div
            tabIndex="0"
            role="button"
            className={styles.onboarding_submitButton_wrapper}
            onKeyDown={this.handleKeyPress}
          >
            <ButtonWrapper
              aria-label="submitButton"
              disabled={this.state.isSubmitting}
              className={submitButtonClasses}
              onClick={this.handleSubmit}
              onKeyDown={this.handleKeyPress}
              data-aid="gladlyChatOnboardingSubmitButton"
              tabIndex="-1"
            >
              {this.props.submitButton}
            </ButtonWrapper>
          </div>
        </div>
      </div>
    );
  }
}

export default Onboarding;

function isValidEmail(email) {
  return EmailValidator.validate(email.trim());
}

const IntroMessageWrapper = overrideStyle('div')`
  font-family: ${p => p.theme.fontLibrary.primary};
`;

const ButtonWrapper = overrideStyle('button')`
  font-family: ${p => p.theme.fontLibrary.primary};
`;
